import { ShipmentMethod as CrewReturnShipmentTypeEnum } from 'corso-types';
import { isExchangeClaim } from '~/utils/claimLineItem';
import { ExchangeMethod, exchangeMethodLabel } from '~/utils/constants';
import { messagingInDays } from '~/utils/formatters';
import { makeCrewClaimWithClaimLineItemsLoader } from '~/utils/routing';

type CrewContext = Parameters<
  Parameters<typeof makeCrewClaimWithClaimLineItemsLoader>[0]
>[0]['context'];

const checkInstantExchangeAvailable = (context: CrewContext) => {
  const {
    settings,
    order,
    crewClaim: { claimLineItems, returnMethod },
  } = context;

  const stripeConfigured = Boolean(settings.stripeConfig);
  const instantExchangeOffered = Boolean(order?.offerInstantExchange);
  const allExchangeClaims = claimLineItems.every(isExchangeClaim);
  const hasReturnLabel =
    returnMethod?.type === CrewReturnShipmentTypeEnum.label;

  // TODO: Revisit how the Instant Exchange interacts with the 'PAYMENT_AND_PARTIAL_REFUND' method
  const isCompatibleExchangeMethod = claimLineItems
    .filter(isExchangeClaim)
    .every(
      (cli) =>
        cli.advancedExchangeMethod === 'NO_PAYMENT_OR_REFUND' ||
        cli.advancedExchangeMethod === 'PAYMENT',
    );

  return (
    isCompatibleExchangeMethod &&
    stripeConfigured &&
    instantExchangeOffered &&
    allExchangeClaims &&
    hasReturnLabel
  );
};

const getDefaultExchangeMethod = (
  offeredMethods: ReturnType<typeof getOfferedExchangeMethods>,
) =>
  offeredMethods.some((method) => method.type === 'instant') ?
    ExchangeMethod.instant
  : ExchangeMethod.standard;

type ExchangeOption = {
  type: ExchangeMethod;
  label: string;
  description: string;
};
const getOfferedExchangeMethods = (
  isInstantExchangeAvailable: boolean,
  timeFrame: number,
): ExchangeOption[] =>
  [
    isInstantExchangeAvailable ?
      {
        type: ExchangeMethod.instant,
        label: exchangeMethodLabel[ExchangeMethod.instant],
        description: `Use a credit card to get your items sent immediately. You will only be charged if you do not return your items within ${messagingInDays(
          timeFrame,
        )}.`,
      }
    : null,
    {
      type: ExchangeMethod.standard,
      label: exchangeMethodLabel[ExchangeMethod.standard],
      description: 'Send your items back first, and get your new items later.',
    },
  ].filter(Boolean);

export function getExchangeMethods(
  context: CrewContext,
  exchangeMethodParam?: ExchangeMethod | null,
) {
  const offered = getOfferedExchangeMethods(
    checkInstantExchangeAvailable(context),
    context.order?.offerInstantExchange?.timeToReturnBeforeCharging ?? 0,
  );
  return {
    offered,
    selected: exchangeMethodParam ?? getDefaultExchangeMethod(offered),
  };
}
